import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, Loader2, Quote } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SERVICES } from "@/lib/constants";

const Stars = ({ value, onChange, size = 18, interactive = false }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type={interactive ? "button" : undefined}
          disabled={!interactive}
          onMouseEnter={() => interactive && setHover(n)}
          onMouseLeave={() => interactive && setHover(0)}
          onClick={() => interactive && onChange(n)}
          data-testid={interactive ? `review-star-${n}` : undefined}
          className={interactive ? "cursor-pointer" : "cursor-default"}
          aria-label={`${n} star`}
        >
          <Star
            size={size}
            className={
              (hover || value) >= n ? "fill-hazard text-hazard" : "text-surface-2"
            }
          />
        </button>
      ))}
    </div>
  );
};

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", rating: 0, service: "", comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const inputCls =
    "w-full bg-ink border border-surface-2 px-4 py-3 text-white placeholder:text-zinc-600 font-sans text-base focus:outline-none focus:border-hazard focus:ring-1 focus:ring-hazard transition-colors";

  const load = () => {
    api
      .get("/reviews")
      .then((r) => setReviews(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.name.trim() && form.rating > 0 && form.comment.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thanks! Your review has been submitted for approval.");
      setForm({ name: "", rating: 0, service: "", comment: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" data-testid="reviews" className="relative bg-ink py-20 md:py-32 border-t border-surface-2">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 md:mb-20">
          <div>
            <h2 className="font-display uppercase leading-[0.85] text-5xl md:text-7xl lg:text-8xl">
              Reviews
            </h2>
          </div>
          <p className="max-w-sm text-zinc-400 text-base leading-relaxed">
            Straight from the customers who trust us with their vehicles. Reviews are
            published after we verify them.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1.3fr_0.7fr] gap-10 lg:gap-16">
          {/* reviews list */}
          <div>
            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="animate-spin text-hazard" size={26} />
              </div>
            ) : reviews.length === 0 ? (
              <div data-testid="no-reviews" className="border border-surface-2 py-16 px-8 text-zinc-500">
                <Quote size={32} className="text-surface-2 mb-4" />
                No reviews published yet. Be the first to leave one!
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-px bg-surface-2 border border-surface-2">
                {reviews.map((r, i) => (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
                    data-testid={`review-card-${r.id}`}
                    className="bg-ink p-6 md:p-8 flex flex-col"
                  >
                    <Stars value={r.rating} />
                    <p className="mt-4 text-zinc-200 text-base leading-relaxed flex-1">
                      "{r.comment}"
                    </p>
                    <div className="mt-6 pt-4 border-t border-surface-2">
                      <div className="font-display text-2xl tracking-wide">{r.name}</div>
                      {r.service && (
                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-hazard mt-1">
                          {r.service}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* form */}
          <div className="bg-surface border border-surface-2 p-6 md:p-8 h-fit lg:sticky lg:top-28">
            <h3 className="font-display uppercase text-3xl tracking-wide mb-6">
              Leave a Review
            </h3>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  data-testid="review-name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  className={inputCls}
                  placeholder="Name"
                />
              </div>
              <div>
                <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">
                  Rating
                </label>
                <Stars value={form.rating} onChange={(n) => set("rating", n)} size={28} interactive />
              </div>
              <div>
                <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">
                  Service (optional)
                </label>
                <select
                  data-testid="review-service"
                  value={form.service}
                  onChange={(e) => set("service", e.target.value)}
                  className={inputCls}
                >
                  <option value="">— Select —</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.slug}>
                      {s.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">
                  Your Review
                </label>
                <textarea
                  rows={4}
                  data-testid="review-comment"
                  value={form.comment}
                  onChange={(e) => set("comment", e.target.value)}
                  className={`${inputCls} resize-none`}
                  placeholder="How did we go?"
                />
              </div>
              <button
                type="submit"
                data-testid="review-submit-btn"
                disabled={!valid || submitting}
                className="w-full flex items-center justify-center gap-2 bg-hazard text-black py-3.5 font-mono text-sm tracking-[0.15em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : "Submit Review"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
